/**
 * First-visit onboarding (DESIGN 6.7): a four-step card in the lobby.
 * 1. Q F + LMB at a dummy, 2. A + Shift+LMB, 3. W + middle click, 4. Q on a friend
 * then A, "see what happens". Each step watches the game events and ticks
 * itself off when the player actually does it, so the card teaches by
 * doing rather than by reading. Dismissable; a localStorage flag keeps it
 * from coming back. With touch controls on (html.touch) each step words the
 * same move with the on-screen buttons and touch and hold instead of keys
 * and mouse buttons.
 */

import type { GameEvent } from '@shared/index.js';
import type { ClientState } from '../state/clientState.js';
import type { Socket } from '../net/socket.js';
import { type Child, type Screen, button, chip, clear, el, keycap, setText, storageGet, storageSet } from './dom.js';
import { STORAGE_KEYS } from './helpers.js';

export interface OnboardingStep {
  title: string;
  /** Builds the instruction line (fresh nodes each render); `touch` picks the on-screen-controls wording. */
  body(touch: boolean): Child[];
  /** True when this event shows the local player (pid) did the step. */
  done(ev: GameEvent, pid: number): boolean;
}

export const ONBOARDING_STEPS: readonly OnboardingStep[] = [
  {
    title: 'Steam a dummy',
    body: (touch) => touch
      ? ['Tap ', chip('water', 'Water'), ' then ', chip('fire', 'Fire'), ' (they fuse into ', chip('steam', 'Steam'), '), then touch and hold on a dummy.']
      : ['Press ', chip('water'), ' then ', chip('fire'), ' (they fuse into ', chip('steam'), '), then hold ', keycap('LMB'), ' aimed at a dummy.'],
    done: (ev, pid) => ev.e === 'cast' && ev.p === pid && ev.kind === 'spray' && ev.el.includes('steam'),
  },
  {
    title: 'Shock everything around you',
    body: (touch) => touch
      ? ['Tap ', chip('lightning', 'Lightning'), ', then ', keycap('Area'), '. Same element, different cast: a nova instead of an arc.']
      : ['Press ', chip('lightning'), ', then ', keycap('Shift + LMB'), '. Same element, different cast: a nova instead of an arc.'],
    done: (ev, pid) => ev.e === 'cast' && ev.p === pid && ev.kind === 'nova' && ev.el.includes('lightning'),
  },
  {
    title: 'Heal yourself',
    body: (touch) => touch
      ? ['Tap ', chip('life', 'Life'), ', then ', keycap('Self'), '. Self-cast turns a healing beam into a heal on you.']
      : ['Press ', chip('life'), ', then ', keycap('Middle click'), '. Self-cast turns a healing beam into a heal on you.'],
    done: (ev, pid) => ev.e === 'cast' && ev.p === pid && ev.kind === 'self' && ev.el.includes('life'),
  },
  {
    title: 'Wet a friend, then zap',
    body: (touch) => touch
      ? ['Tap ', chip('water', 'Water'), ' and touch and hold on a friend (or a dummy) to spray them, then tap ', chip('lightning', 'Lightning'), ' and touch and hold again. See what happens.']
      : ['Press ', chip('water'), ' and spray a friend (or a dummy) with ', keycap('LMB'), ', then press ', chip('lightning'), ' and ', keycap('LMB'), '. See what happens.'],
    done: (ev, pid) => ev.e === 'cast' && ev.p === pid && ev.kind === 'arc',
  },
];

/** Delay between a step completing and the next one appearing, so the tick is seen. */
const ADVANCE_MS = 700;

/** main.ts sets html.touch when touch controls switch on (a touchscreen laptop does it on first touch). */
function touchControlsOn(): boolean {
  return document.documentElement.classList.contains('touch');
}

export function isOnboarded(): boolean {
  return storageGet(STORAGE_KEYS.onboarded) === '1';
}

export function mount(root: HTMLElement, state: ClientState, socket: Socket): Screen {
  void socket;
  const counter = el('div', { class: 'onb-counter' });
  const title = el('h3', { class: 'onb-title' });
  const body = el('p', { class: 'onb-body' });
  const check = el('div', { class: 'onb-check' }, 'Done');
  const nextBtn = button('Next', () => advance(), 'btn btn-primary btn-sm');
  const skipBtn = button('Skip', () => finish(), 'btn btn-ghost btn-sm');
  const card = el('div', { class: 'onb-card', role: 'dialog', 'aria-label': 'First steps' },
    el('div', { class: 'onb-head' }, el('span', { class: 'label' }, 'First steps'), counter),
    title,
    body,
    el('div', { class: 'onb-actions' }, check, skipBtn, nextBtn),
  );
  clear(root);
  root.classList.add('onboarding');
  root.appendChild(card);

  let step = 0;
  let suppressed = false;
  let done = isOnboarded();
  let completedAt: number | null = null;
  let rendered = -1;
  let renderedTouch = false;

  function render(): void {
    const s = ONBOARDING_STEPS[step];
    if (!s) return;
    rendered = step;
    renderedTouch = touchControlsOn();
    setText(counter, `${step + 1} / ${ONBOARDING_STEPS.length}`);
    setText(title, s.title);
    clear(body);
    body.append(...s.body(renderedTouch).map((c) => (c instanceof Node ? c : document.createTextNode(String(c ?? '')))));
    setText(nextBtn, step === ONBOARDING_STEPS.length - 1 ? 'Finish' : 'Next');
    check.hidden = true;
    card.classList.remove('onb-done');
  }

  function markDone(): void {
    if (completedAt !== null) return;
    completedAt = performance.now();
    check.hidden = false;
    card.classList.add('onb-done');
  }

  function advance(): void {
    completedAt = null;
    if (step >= ONBOARDING_STEPS.length - 1) {
      finish();
      return;
    }
    step++;
    render();
  }

  function finish(): void {
    done = true;
    storageSet(STORAGE_KEYS.onboarded, '1');
    root.hidden = true;
  }

  const unsubscribe = state.subscribe((vm) => {
    const visible = !done && !suppressed && vm.room !== null && vm.phase === 'lobby';
    if (root.hidden !== !visible) root.hidden = !visible;
    if (!visible) return;
    if (rendered !== step || renderedTouch !== touchControlsOn()) render();
    const s = ONBOARDING_STEPS[step];
    if (s && completedAt === null) {
      for (const ev of vm.events) {
        if (s.done(ev, state.pid)) {
          markDone();
          break;
        }
      }
    }
    if (completedAt !== null && performance.now() - completedAt >= ADVANCE_MS) advance();
  });

  root.hidden = true;
  return {
    show(): void { suppressed = false; },
    hide(): void { suppressed = true; root.hidden = true; },
    dispose(): void {
      unsubscribe();
      clear(root);
    },
  };
}
